import { Box, Button, Progress, Text, VStack } from '@chakra-ui/react';
import { Form, TextField } from 'components/common/Form';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { PageContext } from 'components/common/PageContext';
import { useSessionPlanGeneration } from 'hooks/api/backend/session-plan';
import Joi from 'joi';
import { ReactElement, useEffect } from 'react';
import { FieldError } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';

type FormValues = {
  userInput: string;
};

const schema = Joi.object({
  userInput: Joi.string().trim().required().messages({
    'string.empty': errorMessage.empty,
    'any.required': errorMessage.empty,
  }),
});

const EXAMPLE_PLACEHOLDER =
  'z. B. Erstelle einen Unterrichtsentwurf zum Thema Blutdruckmessung für Auszubildende im 1. Ausbildungsjahr (90 Minuten).';

export const SessionPlanCreatePage = (): ReactElement => {
  const navigate = useNavigate();
  const {
    generate,
    completedPlanId,
    isLoading,
    isPolling,
    progress,
    progressMessage,
    error,
    reset,
  } = useSessionPlanGeneration();

  const isBusy = isLoading || isPolling;

  useEffect(() => {
    if (!completedPlanId) return;
    navigate(`${ProtectedRoutes.SESSION_PLAN}/${completedPlanId}`, { replace: true });
  }, [completedPlanId, navigate]);

  const handleSubmit = (data: FormValues): void => {
    generate(data.userInput.trim());
  };

  return (
    <PageContext
      title="Neue Unterrichtsplanung"
      headTitle="Neue Unterrichtsplanung"
      hasBackButton
      customOnBackClick={() => navigate(ProtectedRoutes.SESSION_PLAN)}
    >
      <Box bg="background.canvas" minH="100%" px={{ base: 'm', md: 'l' }} py="l" maxW="800px">
        <VStack align="stretch" gap="l">
          <Text textStyle="body.m.regular" color="text.secondary">
            Beschreibe deine Unterrichtseinheit in Freitext. Die KI erstellt daraus einen
            strukturierten Unterrichtsentwurf mit Phasen, Lernzielen und Materialien.
          </Text>

          {!isPolling && !completedPlanId && (
            <Form<FormValues> onSubmit={handleSubmit} schema={schema} gap={4}>
              {({ register, formState }) => (
                <>
                  <TextField
                    label="Unterrichtsidee"
                    placeholder={EXAMPLE_PLACEHOLDER}
                    registration={register('userInput')}
                    error={formState.errors.userInput as FieldError | undefined}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isBusy}
                    disabled={isBusy}
                    alignSelf="flex-start"
                  >
                    Unterrichtsplanung erstellen
                  </Button>
                </>
              )}
            </Form>
          )}

          {isPolling && (
            <Box>
              <Progress.Root
                value={progress}
                size="sm"
                colorPalette="green"
                borderRadius="md"
                mb="xs"
              >
                <Progress.Track>
                  <Progress.Range />
                </Progress.Track>
              </Progress.Root>
              <Text textStyle="body.s.regular" color="text.secondary">
                {progressMessage} ({progress}%)
              </Text>
            </Box>
          )}

          {error && (
            <Box
              bg="background.secondary"
              borderRadius="md"
              p="m"
              border="1px solid"
              borderColor="border.error"
            >
              <Text textStyle="body.s.regular" color="text.error" mb="s">
                {error}
              </Text>
              <Button variant="secondary" size="m" onClick={reset}>
                Erneut versuchen
              </Button>
            </Box>
          )}
        </VStack>
      </Box>
    </PageContext>
  );
};
